import { FC, useState } from 'react';

import { GridColumn, GridLayout, GridRow, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { AuctionActions } from 'components/AuctionBuilder/AuctionActions/AuctionActions';
import { AuctionBid } from 'components/AuctionBuilder/AuctionBid/AuctionBid';
import { AuctionBudget } from 'components/AuctionBuilder/AuctionBudget/AuctionBudget';
import { AuctionBudgetAutoStrategyAdvice } from 'components/AuctionBuilder/AuctionBudget/AuctionBudgetAutoStrategyAdvice/AuctionBudgetAutoStrategyAdvice';
import { AuctionForecastCard } from 'components/AuctionBuilder/AuctionForecastCard/AuctionForecastCard';
import { AuctionPeriod } from 'components/AuctionBuilder/AuctionPeriod/AuctionPeriod';
import { BiddingStrategySelect } from 'components/AuctionBuilder/BiddingStrategySelect/BiddingStrategySelect';
import BalanceReplenishmentModal from 'components/common/BalanceReplenishmentModal/BalanceReplenishmentModal';
import { StatisticCard } from 'components/common/CampaignStatisticCard/StatisticCard';
import { StickyActionBarWrapper } from 'components/common/StickyActionBarWrapper/StickyActionBarWrapper';

import { auctionSelector } from 'store/models/auctionData';
import { useSelector } from 'store/utils';

const TrlKeys = {
    description: 'AuctionBuilder.description',
};

export const AuctionBuilder: FC = () => {
    const isDynamicPriceEnabled = useSelector(({ user }) => user.user?.currentAccount.dynamicPriceEnabled);
    const auctionCampaign = useSelector(auctionSelector.auctionCampaign);
    const campaignId = auctionCampaign?.campaignId;

    const [
        isBalanceReplenishmentModalVisible,
        setIsBalanceReplenishmentModalVisibleOn,
        setIsBalanceReplenishmentModalVisibleOff,
    ] = useOnOffState(false);
    const [loading, setLoading] = useState(false);

    const trls = useTrls();
    const { isMobile, isGtM } = useBreakpoint();

    return (
        <>
            <StickyActionBarWrapper
                content={
                    <GridLayout>
                        <GridRow>
                            <GridColumn xs={4} s={8} m={isGtM ? 6 : 8}>
                                <Text typography="paragraph-2-regular">{trls(TrlKeys.description)}</Text>
                                {isDynamicPriceEnabled && (
                                    <>
                                        <VSpacing default={32} />
                                        <BiddingStrategySelect />
                                    </>
                                )}
                                <VSpacing default={32} />
                                <AuctionBid setLoading={setLoading} loading={loading} />
                                <VSpacing default={32} />
                                <AuctionPeriod />
                                <VSpacing default={32} />
                                <AuctionBudget />
                                <AuctionBudgetAutoStrategyAdvice />
                            </GridColumn>
                            <GridColumn xs={4} s={8} m={isGtM ? 2 : 0} />
                            {isMobile && <VSpacing default={32} />}
                            <GridColumn xs={4} s={8} m={4}>
                                <AuctionForecastCard loading={loading} />
                                {!!auctionCampaign && <StatisticCard campaignId={campaignId} />}
                            </GridColumn>
                        </GridRow>
                        <VSpacing default={32} />
                    </GridLayout>
                }
                actionBar={
                    <AuctionActions
                        loading={loading}
                        setLoading={setLoading}
                        showBalanceReplenishmentModal={setIsBalanceReplenishmentModalVisibleOn}
                    />
                }
            />
            <BalanceReplenishmentModal
                isVisible={isBalanceReplenishmentModalVisible}
                closeModal={setIsBalanceReplenishmentModalVisibleOff}
            />
        </>
    );
};
